import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { HallOfFame, HallRecord } from '../lib/types';
import { MotifLayer } from '../components/badges/badgeArt';
import { ErrorState, LoadingGrid, Panel } from '../components/ui';
import { formatDate, formatNumber } from '../lib/format';

/**
 * The Hall of Fame.
 *
 * All-time records first, then archived seasons newest first — which is how
 * anybody reads a hall of fame: what has never been beaten, then last year.
 *
 * Unclaimed records are shown as empty plinths. A hall with a gap in it is an
 * invitation, and hiding the ones nobody has taken would mean the page only
 * ever showed you what you had already missed.
 */

export function HallOfFamePage() {
  const state = useAsync<HallOfFame>(() => api.get('/hall-of-fame'), []);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={6} height={130} />;

  const { catalog, allTime, archives } = state.data;
  const claimed = new Map(allTime.map((record) => [record.key, record]));
  const firsts = catalog.filter((entry) => entry.kind === 'FIRST');

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Hall of Fame</h1>
          <div className="page-head__sub">
            {allTime.length} of {firsts.length} all-time records claimed
          </div>
        </div>
      </div>

      <Panel title="All time" hint="Claimed once, and never moved" raised={false}>
        <div className="hof">
          {firsts.map((entry) => {
            const record = claimed.get(entry.key);
            return (
              <article
                key={entry.key}
                className={`hof__plinth hof__plinth--${entry.accent} ${record ? '' : 'hof__plinth--empty'}`}
              >
                <div className="hof__emblem" aria-hidden>
                  <svg viewBox="0 0 16 16" width={38} height={38} shapeRendering="crispEdges">
                    <MotifLayer motif={entry.motif} fill="currentColor" highlight="#fff" />
                  </svg>
                </div>
                <h3 className="hof__name">{entry.name}</h3>
                {record ? (
                  <>
                    <Winner record={record} />
                    <p className="tiny muted">{entry.description}</p>
                  </>
                ) : (
                  <>
                    <div className="hof__unclaimed mono">Unclaimed</div>
                    <p className="tiny muted">{entry.description}</p>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </Panel>

      {archives.length === 0 ? (
        <p className="tiny muted">
          No season has ended yet. When one does, its leaders are archived here rather than
          disappearing when the boards reset.
        </p>
      ) : (
        archives.map((season) => (
          <Panel
            key={season.key}
            title={season.name}
            hint={`${formatDate(season.startsAt)} – ${formatDate(season.endsAt)}`}
            raised={false}
          >
            <div className="hof hof--season">
              {season.records.map((record) => (
                <article key={record.key} className={`hof__plinth hof__plinth--${record.accent}`}>
                  <div className="hof__emblem" aria-hidden>
                    <svg viewBox="0 0 16 16" width={30} height={30} shapeRendering="crispEdges">
                      <MotifLayer motif={record.motif} fill="currentColor" highlight="#fff" />
                    </svg>
                  </div>
                  <h3 className="hof__name">{record.name}</h3>
                  <Winner record={record} />
                </article>
              ))}
            </div>
          </Panel>
        ))
      )}
    </div>
  );
}

/**
 * The winner line.
 *
 * `displayName` is stored on the record rather than joined from the account, so
 * a deleted trainer still holds their record — which means the name is
 * sometimes not a link, and that is correct rather than a missing feature.
 */
function Winner({ record }: { record: HallRecord }) {
  return (
    <div className="hof__winner">
      {record.userId ? (
        <Link to={`/trainer/${record.displayName}`} className="hof__trainer">
          {record.displayName}
        </Link>
      ) : (
        <span className="hof__trainer">{record.displayName}</span>
      )}
      <div className="tiny muted">
        {formatDate(record.achievedAt)}
        {record.value > 0 && ` · ${formatNumber(record.value)}`}
        {record.detail && ` · ${record.detail}`}
      </div>
    </div>
  );
}
